import classNames from "classnames";
import Tooltip from "components/Popover/Tooltip/Tooltip";
import React from "react";

export interface NavigationTab {
  id: string;
  label: string;
  icon?: string | React.ReactNode;
  rightElement?: React.ReactNode;
  hidden?: boolean;
  disabled?: boolean;
  tooltip?: React.ReactNode;
}

interface NavigationTabsProps {
  tabs: NavigationTab[];
  selectedTab: string;
  onTabClick: (tabId: string) => void;
  className?: string;
  tabClassName?: string;
  activeTabClassName?: string;
  inactiveTabClassName?: string;
  sticky?: boolean;
}

/**
 * NavigationTabs is a component that renders a list of tabs.
 * It is used to navigate between different tabbed sections.
 */
export const NavigationTabs: React.FC<NavigationTabsProps> = ({
  tabs,
  selectedTab,
  onTabClick,
  className = "",
  tabClassName = "",
  activeTabClassName = "text-fg-base font-medium after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[3px] after:bg-fg-base",
  inactiveTabClassName = "text-fg-muted hover:text-fg-base",
  sticky = false,
}) => {
  const navClassName = classNames(
    "w-full border-b border-border-base",
    sticky ? "sticky top-14 bg-bg-base z-10 px-6" : "",
    className ? className : "border-b border-border-base mb-3"
  );

  return (
    <nav className={navClassName}>
      <div className="flex gap-6 min-w-max">
        {tabs.map(
          ({ id, label, icon, rightElement, hidden, disabled, tooltip }) => {
            const tabContent = (
              <div className="flex items-center gap-2 rounded-xl px-3 mx-[-12px] py-1 hover:bg-bg-faint">
                {icon &&
                  (typeof icon === "string" ? (
                    <i className={`text-md ${icon}`} />
                  ) : (
                    icon
                  ))}
                {label}
                {rightElement}
              </div>
            );

            return (
              !hidden && (
                <div key={id} className="relative">
                  {tooltip ? (
                    <Tooltip
                      tooltipContent={<>{tooltip}</>}
                      placement="top"
                      className="w-80"
                    >
                      <button
                        onClick={() => onTabClick(id)}
                        className={classNames(
                          "py-2 relative whitespace-nowrap",
                          disabled ? "pointer-events-none" : "",
                          selectedTab === id
                            ? activeTabClassName
                            : inactiveTabClassName,
                          tabClassName
                        )}
                        disabled={disabled}
                      >
                        {tabContent}
                      </button>
                    </Tooltip>
                  ) : (
                    <button
                      onClick={() => onTabClick(id)}
                      className={classNames(
                        "py-2 relative whitespace-nowrap",
                        disabled ? "pointer-events-none" : "",
                        selectedTab === id
                          ? activeTabClassName
                          : inactiveTabClassName,
                        tabClassName
                      )}
                      disabled={disabled}
                    >
                      {tabContent}
                    </button>
                  )}
                </div>
              )
            );
          }
        )}
      </div>
    </nav>
  );
};

export default NavigationTabs;
